/**
 * DateTimeRangePicker — 날짜+시간 범위 선택기 (빠른 검색 + 범위 + 헤더 드릴다운)
 *
 * 팝오버 = 좌측 프리셋 리스트 + 우측 [From/To 세그먼트 + 캘린더(CalendarCore) + 시/분].
 * From/To 를 세그먼트로 전환하며 각각의 날짜·시간을 고르고, "적용"으로 확정한다.
 *
 * onChange 는 Date 객체 쌍을 넘긴다 — ISO/UTC 를 강제하지 않으므로
 * 호출부가 로컬(KST) 벽시계 문자열 등 원하는 포맷으로 변환할 수 있다.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cn } from './cn'
import { CalendarCore } from './CalendarCore'
import { toDateTime } from './DatePicker'
import { type DateTimeRangePreset } from '../lib/dateRange'
import styles from './DateTimeRangePicker.module.css'

export interface DateTimeRange {
  from: Date | null
  to: Date | null
}

export interface DateTimeRangePickerProps {
  /** 선택된 범위. Date 또는 파싱 가능한 문자열 */
  value?: { from: string | Date | null; to: string | Date | null } | null
  /** 확정(적용) 시 호출. presetKey 는 프리셋 선택 시에만 non-null */
  onChange?: (range: DateTimeRange, meta: { presetKey: string | null }) => void
  /** 빠른 검색 프리셋 (시간 포함) */
  presets?: DateTimeRangePreset[]
  /** 하이라이트할 프리셋 키 (제어형) */
  activePresetKey?: string | null
  /** 분 단위 간격 (기본 5) */
  minuteStep?: number
  /** 최소 선택 가능 날짜 */
  minDate?: Date
  /** 최대 선택 가능 날짜 */
  maxDate?: Date
  /** 입력 크기 */
  size?: 'sm' | 'md'
  /** 비활성 상태 */
  disabled?: boolean
  /** 필드 레이블 */
  label?: string
  /** 플레이스홀더 */
  placeholder?: string
  /** 추가 클래스명 */
  className?: string
}

const pad = (n: number) => String(n).padStart(2, '0')

function buildHourOptions(): { value: number; label: string }[] {
  const opts: { value: number; label: string }[] = []
  for (let h = 0; h < 24; h++) {
    const period = h < 12 ? '오전' : '오후'
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h
    opts.push({ value: h, label: `${period} ${display}시` })
  }
  return opts
}

function buildMinuteOptions(step: number): { value: number; label: string }[] {
  const opts: { value: number; label: string }[] = []
  for (let m = 0; m < 60; m += step) {
    opts.push({ value: m, label: `${pad(m)}분` })
  }
  return opts
}

function displayDateTime(d: Date): string {
  return `${d.getFullYear()}.${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

type Endpoint = 'from' | 'to'

export function DateTimeRangePicker({
  value,
  onChange,
  presets = [],
  activePresetKey,
  minuteStep = 5,
  minDate,
  maxDate,
  size = 'md',
  disabled = false,
  label,
  placeholder = '기간을 선택하세요',
  className,
}: DateTimeRangePickerProps) {
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState<Endpoint>('from')
  const wrapperRef = useRef<HTMLDivElement>(null)

  // 외부 value → 안정적 Date 참조 (timestamp 메모이즈)
  const valueFromTime = (value?.from ? toDateTime(value.from) : null)?.getTime() ?? null
  const valueToTime = (value?.to ? toDateTime(value.to) : null)?.getTime() ?? null

  // draft (팝오버 열려있는 동안 편집 중인 값)
  const [draftFrom, setDraftFrom] = useState<Date | null>(null)
  const [draftTo, setDraftTo] = useState<Date | null>(null)

  useEffect(() => {
    if (open) {
      setDraftFrom(valueFromTime != null ? new Date(valueFromTime) : null)
      setDraftTo(valueToTime != null ? new Date(valueToTime) : null)
      setEditing('from')
    }
  }, [open, valueFromTime, valueToTime])

  const hourOptions = useMemo(() => buildHourOptions(), [])
  const minuteOptions = useMemo(() => buildMinuteOptions(minuteStep), [minuteStep])

  const current = editing === 'from' ? draftFrom : draftTo
  const setCurrent = editing === 'from' ? setDraftFrom : setDraftTo

  const handleToggle = useCallback(() => {
    if (!disabled) setOpen((v) => !v)
  }, [disabled])

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  // 날짜 선택 → 기존 시:분 유지, From 선택 후엔 자동으로 To 편집으로 전환
  const handleSelectDay = useCallback((d: Date) => {
    const base = current ?? new Date(d.getFullYear(), d.getMonth(), d.getDate(), editing === 'from' ? 0 : 23, editing === 'from' ? 0 : 59, 0, 0)
    const next = new Date(d.getFullYear(), d.getMonth(), d.getDate(), base.getHours(), base.getMinutes(), 0, 0)
    setCurrent(next)
    if (editing === 'from' && !draftTo) setEditing('to')
  }, [current, editing, setCurrent, draftTo])

  const handleHourSelect = useCallback((h: number) => {
    const base = current ?? new Date()
    const next = new Date(base.getFullYear(), base.getMonth(), base.getDate(), h, base.getMinutes(), 0, 0)
    setCurrent(next)
  }, [current, setCurrent])

  const handleMinuteSelect = useCallback((m: number) => {
    const base = current ?? new Date()
    const next = new Date(base.getFullYear(), base.getMonth(), base.getDate(), base.getHours(), m, 0, 0)
    setCurrent(next)
  }, [current, setCurrent])

  const handlePreset = useCallback((preset: DateTimeRangePreset) => {
    const { from, to } = preset.getRange()
    onChange?.({ from, to }, { presetKey: preset.key })
    setOpen(false)
  }, [onChange])

  const draftValid = draftFrom != null && draftTo != null && draftFrom <= draftTo

  const handleApply = useCallback(() => {
    if (!draftFrom || !draftTo) return
    const [lo, hi] = draftFrom <= draftTo ? [draftFrom, draftTo] : [draftTo, draftFrom]
    onChange?.({ from: lo, to: hi }, { presetKey: null })
    setOpen(false)
  }, [draftFrom, draftTo, onChange])

  const resolvedActiveKey = activePresetKey ?? null

  const triggerText = valueFromTime != null && valueToTime != null
    ? `${displayDateTime(new Date(valueFromTime))} ~ ${displayDateTime(new Date(valueToTime))}`
    : null

  const curHour = current?.getHours() ?? (editing === 'from' ? 0 : 23)
  const curMinute = current ? Math.round(current.getMinutes() / minuteStep) * minuteStep : (editing === 'from' ? 0 : 55)

  return (
    <div ref={wrapperRef} className={cn(styles.wrapper, className)}>
      {label && <label className={styles.label}>{label}</label>}

      <button
        type="button"
        className={cn(styles.trigger, styles[size], disabled && styles.disabled)}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={cn(styles.triggerText, !triggerText && styles.placeholder)}>
          {triggerText ?? placeholder}
        </span>
        <svg className={styles.calendarIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="4" width="18" height="18" rx="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={styles.popover}>
          {presets.length > 0 && (
            <div className={styles.presetList}>
              <span className={styles.presetTitle}>빠른 검색</span>
              {presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  className={cn(styles.presetBtn, resolvedActiveKey === p.key && styles.presetActive)}
                  onClick={() => handlePreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}

          <div className={styles.editArea}>
            {/* From / To 세그먼트 */}
            <div className={styles.segment}>
              <button
                type="button"
                className={cn(styles.segmentBtn, editing === 'from' && styles.segmentActive)}
                onClick={() => setEditing('from')}
              >
                시작 {draftFrom ? displayDateTime(draftFrom) : '—'}
              </button>
              <button
                type="button"
                className={cn(styles.segmentBtn, editing === 'to' && styles.segmentActive)}
                onClick={() => setEditing('to')}
              >
                종료 {draftTo ? displayDateTime(draftTo) : '—'}
              </button>
            </div>

            <div className={styles.body}>
              <div className={styles.calendarArea}>
                <CalendarCore
                  mode="range"
                  rangeStart={draftFrom}
                  rangeEnd={draftTo}
                  focusDate={current}
                  minDate={minDate}
                  maxDate={maxDate}
                  onSelectDay={handleSelectDay}
                />
              </div>

              {/* 시/분 */}
              <div className={styles.timeSection}>
                <div className={styles.timeColumn}>
                  <div className={styles.timeList}>
                    {hourOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={cn(styles.timeItem, curHour === opt.value && styles.timeItemSelected)}
                        onClick={() => handleHourSelect(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className={styles.timeColumn}>
                  <div className={styles.timeList}>
                    {minuteOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        className={cn(styles.timeItem, curMinute === opt.value && styles.timeItemSelected)}
                        onClick={() => handleMinuteSelect(opt.value)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <button
                type="button"
                className={styles.applyBtn}
                disabled={!draftValid}
                onClick={handleApply}
              >
                적용
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
